import type { TFunction } from 'i18next'
import type { LucideIcon } from 'lucide-react'
import { RoleEnum, TrainerCapability } from '@zeus/index'
import { Accessibility, Award, BellRing, Building2, CalendarClock, CreditCard, FileSpreadsheet, FileText, Inbox, KeyRound, LayoutDashboard, ListChecks, MessageSquareWarning, MessagesSquare, Package, Palette, ShieldCheck, TrendingUp, Users, UsersRound, Webhook } from 'lucide-react'
import { can } from '~/helpers/auth/can'

export type NavItem = { title: string, url: string, icon: LucideIcon }

/** Un groupe de la barre latérale ; sans intertitre quand `label` est nul. */
export type NavGroup = { key: string, label: string | null, items: NavItem[] }

type Entry = NavItem & { adminOnly?: boolean, capability?: TrainerCapability, selfServiceBilling?: boolean }

/**
 * Les onglets du back-office, regroupés par catégorie (#76).
 *
 * Le tableau de bord ouvre la liste, hors groupe : c'est la route par défaut
 * de l'admin comme du formateur, l'entrée par laquelle on revient. Viennent
 * ensuite quatre intertitres, identiques pour les deux rôles ; un onglet qu'un
 * rôle n'a pas quitte simplement son groupe, et un groupe vide disparaît.
 *
 * `adminOnly` reprend, entrée par entrée, les rôles déclarés sur la route dans
 * `router.tsx`. Les deux doivent rester d'accord : une entrée affichée mais
 * interdite par son loader renvoie le formateur sur l'accueil sans rien dire.
 * `capability` fait de même avec la capacité exigée par le loader (#11).
 */
export function backOfficeNav(
  role: RoleEnum | string | undefined,
  capabilities: readonly TrainerCapability[] | null,
  t: TFunction,
  // Facturation masquée aux organisations tant que le paiement n'existe pas (#165).
  { billingSelfService = false }: { billingSelfService?: boolean | null } = {},
): NavGroup[] {
  // Le SuperAdmin (opérateur plateforme) n'appartient à aucune organisation :
  // il ne voit que la gestion des organisations, pas les écrans scopés par org.
  if (role === RoleEnum.SuperAdmin) {
    return [{
      key: 'platform',
      label: t('common.administration'),
      items: [
        { title: t('common.organizations'), url: '/back-office/organizations', icon: Building2 },
        { title: t('trialRequests.title'), url: '/back-office/trial-requests', icon: Inbox },
        { title: t('billing.title'), url: '/back-office/billing', icon: CreditCard },
      ],
    }]
  }

  const isAdmin = role === RoleEnum.Admin
  const groups: { key: string, label: string | null, entries: Entry[] }[] = [
    {
      key: 'home',
      label: null,
      entries: [
        { title: t('common.dashboard'), url: '/back-office/dashboard', icon: LayoutDashboard },
      ],
    },
    {
      // Ce qui prolonge la lecture du tableau de bord : les réponses aux questions
      // (#159) et les rapports à télécharger ou recevoir (#163).
      key: 'statistics',
      label: t('nav.group.statistics'),
      entries: [
        { title: t('questions.title'), url: '/back-office/questions', icon: ListChecks, capability: TrainerCapability.STATISTICS_VIEW },
        { title: t('reports.title'), url: '/back-office/reports', icon: FileSpreadsheet, capability: TrainerCapability.STATISTICS_EXPORT },
      ],
    },
    {
      key: 'training',
      label: t('nav.group.training'),
      entries: [
        { title: t('common.modules'), url: '/back-office/modules', icon: Package, capability: TrainerCapability.MODULES_VIEW },
        { title: t('common.sessions'), url: '/back-office/sessions', icon: CalendarClock, capability: TrainerCapability.SESSIONS_VIEW },
        { title: t('common.certificates'), url: '/back-office/certificates', icon: Award, adminOnly: true },
        // Règlement, programme, livret remis aux apprenants (#326).
        { title: t('documents.title'), url: '/back-office/documents', icon: FileText, adminOnly: true },
        // Questionnaire proposé avant l'attestation (#320).
        { title: t('surveyEditor.title'), url: '/back-office/survey', icon: MessagesSquare, adminOnly: true },
        // Actions d'amélioration et risques qualité (#328).
        { title: t('quality.title'), url: '/back-office/quality', icon: TrendingUp, adminOnly: true },
      ],
    },
    {
      key: 'learners',
      label: t('nav.group.learners'),
      entries: [
        // Même écran pour les deux rôles, mais pas la même population : le
        // formateur n'y voit que ses apprenants et ses pairs, d'où un libellé
        // qui annonce ce qu'il va trouver plutôt que le nom du modèle.
        { title: isAdmin ? t('common.users') : t('common.learners'), url: '/back-office/users', icon: Users, capability: TrainerCapability.LEARNERS_VIEW },
        { title: t('common.groups'), url: '/back-office/groups', icon: UsersRound, adminOnly: true },
        { title: t('common.reminders'), url: '/back-office/reminders', icon: BellRing, adminOnly: true },
        // Admin seulement (#325) : un signalement peut viser un formateur.
        { title: t('complaints.title'), url: '/back-office/complaints', icon: MessageSquareWarning, adminOnly: true },
        // Référent et besoins d'aménagement (#327) : donnée de santé, admin seulement.
        { title: t('accommodations.title'), url: '/back-office/accommodations', icon: Accessibility, adminOnly: true },
      ],
    },
    {
      key: 'administration',
      label: t('common.administration'),
      // Marque, politique d'authentification, droits du formateur, contrat : des décisions d'admin.
      entries: [
        { title: t('common.settings'), url: '/back-office/settings', icon: Palette, adminOnly: true },
        { title: t('common.sso'), url: '/back-office/sso', icon: KeyRound, adminOnly: true },
        { title: t('common.webhooks'), url: '/back-office/webhooks', icon: Webhook, adminOnly: true },
        { title: t('common.trainerCapabilities'), url: '/back-office/trainer-capabilities', icon: ShieldCheck, adminOnly: true },
        { title: t('billing.title'), url: '/back-office/billing', icon: CreditCard, adminOnly: true, selfServiceBilling: true },
      ],
    },
  ]

  return groups
    .map(({ key, label, entries }) => ({
      key,
      label,
      items: entries
        .filter(entry => !entry.adminOnly || isAdmin)
        .filter(entry => !entry.selfServiceBilling || billingSelfService)
        .filter(entry => !entry.capability || can(role, capabilities, entry.capability))
        .map(({ title, url, icon }) => ({ title, url, icon })),
    }))
    .filter(group => group.items.length > 0)
}

/**
 * L'entrée est-elle celle de l'écran ouvert ? Ses sous-pages comptent : la
 * fiche d'une session (#243) garde « Sessions » allumé.
 */
export function isNavItemActive(pathname: string, url: string): boolean {
  return pathname === url || pathname.startsWith(`${url}/`)
}
